<template>
  <div class="card-body p-2">
    <div class="mc-liveview--fo-sidebar-accordion-item">
      <div header-tag="header" role="tab">
        <div v-b-toggle.accordion-1 class="d-flex justify-content-between">
          <div class="card d-flex flex-column gap-1 m-0">
            <div class="d-flex align-items-center gap-2">
              <div
                class="rounded-circle bg-light d-flex align-items-center justify-content-center icon-container"
              >
                <i class="mc-fa icon-fill_cube text-primary"></i>
              </div>
              <div class="d-flex flex-column">
                <div class="d-flex align-items-center gap-2">
                  <h5 class="mb-0">
                    {{ 'Muatan' }}
                  </h5>
                  <McBadge
                    :icon="badgeIcon"
                    :iconVariant="badgeVariant"
                    :text="badgeText"
                    :variant="badgeOutline"
                    size="medium"
                    type="button"
                  />
                </div>
                <small class="text-muted">
                  {{ 'Sesuaikan kuantitas muatan jika ada perbedaan dari rencana' }}
                </small>
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <p
              v-if="isModifyQuantity"
              class="text-muted mb-0 timestamp-text me-2"
            >
              <span>
                <i class="icon-fill_sync"></i>
              </span>
              {{ `Terakhir diperbarui: ${lastUpdateFormatted}` }}
            </p>

            <div>
              <i class="mc-fa icon-fill_arrow-ios-down"></i>
              <i class="mc-fa icon-fill_arrow-ios-up"></i>
            </div>
          </div>
        </div>
      </div>
      <b-collapse
        id="accordion-1"
        accordion="my-accordion"
        role="tabpanel"
        :visible="isComplete"
        @hidden="dataLoaded = false"
      >
        <div
          v-if="isProductTypeLiquid(activity?.delivery_order)"
          class="conversion-card p-2 mt-2 mb-2"
        >
          <p><strong>{{ $t('fleetTask.deliveryLoads.conversion.title') }}</strong></p>
          <span v-if="quantityConversionError" class="error-message">
            {{ quantityConversionError }}
          </span>
          <div class="d-flex">
            <b-col md="4">
              <FormulateInput
                :key="activity?.delivery_order?.id + '-initialQuantity'"
                :ref="`${activity?.delivery_order?.id}-initialQuantity-ref`"
                type="text"
                name="initial-quantity"
                placeholder="Kuantitas Awal"
                :value="getConversionFieldValue(activity?.delivery_order?.id, 'initialQuantity')"
                validation="required"
                :validation-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :error-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :validation-messages="{ required: 'Kuantitas awal wajib diisi' }"
                @input="
                  (val) =>
                    updateConversionField(activity?.delivery_order?.id, 'initialQuantity', val)
                "
              />
            </b-col>
            <b-col md="1" class="w-auto">
              <div class="dash-line"></div>
            </b-col>
            <b-col md="4">
              <FormulateInput
                :key="activity?.delivery_order?.id + '-finalQuantity'"
                :ref="`${activity?.delivery_order?.id}-finalQuantity-ref`"
                type="text"
                name="final-quantity"
                placeholder="Kuantitas Akhir"
                :value="getConversionFieldValue(activity?.delivery_order?.id, 'finalQuantity')"
                validation="required"
                :validation-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :error-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :validation-messages="{ required: 'Kuantitas akhir wajib diisi' }"
                @input="
                  (val) => updateConversionField(activity?.delivery_order?.id, 'finalQuantity', val)
                "
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :key="activity?.delivery_order?.id + '-unitConversion'"
                :ref="`${activity?.delivery_order?.id}-unitConversion-ref`"
                type="select-resourceful"
                name="unit-conversion"
                placeholder="Satuan"
                trackBy="id"
                :optionLabelFn="({ item }) => item.name"
                :optionsGetter="getOptionUnit"
                :value="getConversionFieldValue(activity?.delivery_order?.id, 'unitConversion')"
                validation="required"
                :validation-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :error-behavior="getConversionValidationBehavior(activity?.delivery_order?.id)"
                :validation-messages="{ required: 'Satuan konversi wajib diisi' }"
                @input="
                  (val) =>
                    updateConversionField(activity?.delivery_order?.id, 'unitConversion', val)
                "
              />
            </b-col>
            <b-col md="2">
              <McButton
                variant="primary"
                text="Konversikan"
                class="mt-1"
                :disabled="isConversionButtonDisabled(activity?.delivery_order?.id)"
                @click="handleToConversion(activity)"
              />
            </b-col>
          </div>
        </div>
        <ValueHelpTable
          :isBusy="isLoading"
          :attrs="tableAttrs"
          :listGetter="getDeliveryLoadData"
          :multiple="false"
          :selectable="false"
          :totalCount="totalCount"
        >
          <template #product_name="{ item }">
            <div class="cell-display">
              <div class="primary-display">
                {{ item.product.name }}
              </div>
              <div class="secondary-display">
                {{ item.product.product_type.name }}
              </div>
            </div>
          </template>
          <template #quantity="{ item }">
            <div class="cell-display">
              <div class="primary-display">{{ formatNumber(item.quantity) }} {{ item.uom }}</div>
              <div class="secondary-display">
                {{ formatNumber(item.total_weight) }} {{ 'Kg' }}
                <span class="dot-separator">•</span>
                {{ formatNumber(item.total_volume) }} {{ 'Cbm' }}
              </div>
            </div>
          </template>

          <template #actual="{ item }">
            <div class="cell-display">
              <div class="primary-display d-flex align-items-center">
                <div class="flex-grow-1">
                  <FormulateInput
                    v-if="
                      isPickUp &&
                      !item.quantity_delivered &&
                      item.quantity_picked !== 0 &&
                      item.quantity_delivered !== 0
                    "
                    :ref="`${item.id}-actual-ref`"
                    type="text"
                    :validation="validationRuleActual(item)"
                    :validation-messages="{
                      required: 'Kuantitas wajib diisi',
                      number: 'Harus berupa angka',
                      decimal: 'Angka di belakang koma tidak boleh lebih dari 3 digit',
                      min: 'Kuantitas tidak boleh kurang dari 0',
                      max: item.is_adjusted_picked
                        ? `Kuantitas aktual melebihi perubahan terakhir`
                        : `Kuantitas aktual melebihi rencana`,
                    }"
                    error-behavior="blur"
                    :class="{
                      'yellow-border':
                        modifiedQuantities[`actual-${item.id}`] && 
                        !(
                          fieldErrors[`actual-${item.id}`] &&
                          fieldErrors[`actual-${item.id}`].hasErrors
                        ) &&
                        modifiedQuantities[`actual-${item.id}`] != (item.quantity_picked ?? item.quantity),
                    }"
                    :value="item.quantity_picked ?? (!isProductTypeLiquid(act?.delivery_order)?item.quantity : null)"
                    @input="(val) => handleActualChange(item, val)"
                    @validation="($event) => handleValidation(`actual-${item.id}`, $event)"
                  >
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ item.uom }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                  <div v-else>{{ formatNumber(item.quantity_picked) }} {{ item.uom }}</div>
                </div>
                <i
                  v-if="
                    item.is_adjusted_picked &&
                    isPickUp &&
                    !item.quantity_delivered &&
                    item.quantity_picked !== 0 &&
                    item.quantity_delivered !== 0
                  "
                  :id="`tooltip-actual-${item.id}`"
                  class="mc-fa icon-outline_info ml-2"
                ></i>

                <b-tooltip
                  :target="`tooltip-actual-${item.id}`"
                  triggers="hover"
                  placement="bottom"
                >
                  {{ 'Perubahan Terakhir :' }}
                  <strong> {{ formatNumber(item.quantity_picked) }} {{ item.uom }} </strong>
                </b-tooltip>
              </div>
              <div
                v-if="!isPickUp && isCompletedPickUp && isCompletedDrop"
                class="secondary-display"
                :class="{
                  'modified-value':
                    (
                      modifiedQuantities[`actual-${item.id}`] !=
                      item.quantity
                    ) &&
                    !hasValidationError(`${item.id}-actual-ref`),
                }"
              >
                {{ formatNumber(item.weight_picked) }}
                {{ 'Kg' }}
                <span class="dot-separator">•</span>
                {{ formatNumber(item.volume_picked) }}
                {{ 'Cbm' }}
              </div>
              <div
                v-else
                class="secondary-display"
                :class="{
                  'modified-value':
                    (
                      modifiedQuantities[`actual-${item.id}`] !=
                      item.quantity
                    ) &&
                    !hasValidationError(`${item.id}-actual-ref`),
                }"
              >
                {{ formatNumber(getUpdatedWeight(item, modifiedQuantities[`actual-${item.id}`], 'actual')) }}
                {{ 'Kg' }}
                <span class="dot-separator">•</span>
                {{ formatNumber(getUpdatedVolume(item, modifiedQuantities[`actual-${item.id}`], 'actual')) }}
                {{ 'Cbm' }}
              </div>
            </div>
          </template>

          <template #drop="{ item }">
            <div class="cell-display">
              <div class="primary-display d-flex align-items-center">
                <div class="flex-grow-1">
                  <FormulateInput
                    v-if="shouldShowDropInput(item)"
                    :key="item.id + '-drop'"
                    :ref="`${item.id}-drop-ref`"
                    v-model="modifiedQuantities[`drop-${item.id}`]"
                    type="text"
                    :validation="validationRuleDrop(item)"
                    :validation-messages="{
                      required: 'Kuantitas wajib diisi',
                      number: 'Harus berupa angka',
                      decimal: 'Angka di belakang koma tidak boleh lebih dari 3 digit',
                      min: 'Kuantitas tidak boleh kurang dari 0',
                      max:
                        item.is_adjusted_picked && item.is_adjusted_delivered
                          ? 'Kuantitas aktual terkirim melebihi perubahan terakhir'
                          : item.is_adjusted_picked
                          ? 'Kuantitas aktual terkirim melebihi aktual pengambilan'
                          : item.is_adjusted_delivered
                          ? 'Kuantitas aktual terkirim melebihi perubahan terakhir'
                          : 'Kuantitas aktual terkirim melebihi rencana',
                    }"
                    error-behavior="blur"
                    :show-errors="Boolean(conversionSubmitAttemptedMap[activity?.delivery_order?.id])"
                    :class="{
                      'yellow-border':
                        (
                          modifiedQuantities[`drop-${item.id}`] !=
                          item.quantity_picked
                        ) &&
                        !(
                          fieldErrors[`drop-${item.id}`] && fieldErrors[`drop-${item.id}`].hasErrors
                        ) && (item.quantity_delivered !== null && modifiedQuantities[`drop-${item.id}`] != item.quantity_delivered),
                    }"
                    @input="(val) => handleDropChange(item, val)"
                    @validation="($event) => handleValidation(`drop-${item.id}`, $event)"
                  >
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ item.uom }}</span>
                      </div>
                    </template>
                  </FormulateInput>

                  <div v-else>
                    {{
                      item.quantity_delivered === 0 ? item.quantity_delivered : item.quantity_picked
                    }}
                    {{ item.uom }}
                  </div>
                </div>
                <i
                  v-if="
                    item.is_adjusted_delivered &&
                    !isPickUp &&
                    item.quantity_delivered > 0 &&
                    item.quantity_picked > 0
                  "
                  :id="`tooltip-drop-${item.id}`"
                  class="mc-fa icon-outline_info ml-2"
                ></i>
                <b-tooltip :target="`tooltip-drop-${item.id}`" triggers="hover" placement="bottom">
                  {{ 'Perubahan Terakhir :' }}
                  <strong> {{ formatNumber(item.quantity_delivered) }} {{ item.uom }} </strong>
                </b-tooltip>
              </div>
              <div
                class="secondary-display"
                :class="{
                  'modified-value':
                    (
                      modifiedQuantities[`drop-${item.id}`] !=
                      item.quantity_picked
                    ) &&
                    !hasValidationError(`${item.id}-drop-ref`),
                }"
              >
                {{ formatNumber(
                  getUpdatedWeight(
                    item,
                    modifiedQuantities[`drop-${item.id}`],
                    'drop'
                  )
                ) }}
                {{ 'Kg' }}
                <span class="dot-separator">•</span>
                {{ formatNumber(
                  getUpdatedVolume(
                    item,
                    modifiedQuantities[`drop-${item.id}`],
                    'drop'
                  )
                ) }}
                {{ 'Cbm' }}
              </div>
              <span v-if="getConversionDropError(item)" class="error-message">
                {{ getConversionDropError(item) }}
              </span>
            </div>
          </template>

          <template #reason="{ item }">
            <div class="cell-display">
              <div class="primary-display">
                <div
                  v-if="
                    isSamator && isLiquidLoadItem() ? 
                    (isPickUp && isCompletedPickUp && isCompletedDrop)
                    :
                    (
                      (isPickUp && (item.quantity_delivered === 0 || item.quantity_picked === 0)) ||
                      (isPickUp && isCompletedPickUp && isCompletedDrop) ||
                      (!isPickUp && (item.quantity_picked === 0 || item.quantity_delivered === 0))
                    )
                  "
                >
                  {{ ((isPickUp ? item.adjustment_reason_picked : item.adjustment_reason_delivered) || '').trim() || '-' }}
                </div>
                <div v-else class="custom-form-group">
                  <FormulateInput
                    :ref="`${item.id}-reason-ref`"
                    placeholder="Isi alasan"
                    type="text"
                    :disabled="reasonDisabledMap[item.id] !== false"
                    :validation="shouldRequireReason(item) ? 'required' : ''"
                    :validation-messages="{
                      required: 'Alasan perubahan wajib diisi',
                    }"
                    error-behavior="submit"
                    :class="{ 'error-input': errorFields.includes(item.id) }"
                    @input="(val) => updateReason(item, val)"
                    @validation="($event) => handleValidation(`reason-${item.id}`, $event)"
                  />
                  <span v-if="errorFields.includes(item.id)" class="error-message">
                    {{ 'Alasan perubahan wajib diisi' }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </ValueHelpTable>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import ValueHelpTable from '@/components/formulate/ValueHelpInputV2/components/ModalBody/Table'
import * as Load from '@/services/rest-api/lib/delivery/load'
import { quantityConversion } from '@/services/rest-api/lib/fleet-task/samator'
import { formatDateWithTz } from '@/helpers/tzDate'
import * as historyApi from '@/services/rest-api/lib/delivery/history'
import * as DeliveryNote from '@/services/rest-api/lib/customer/delivery-note'
import moment from 'moment'
import { formatNumber } from '@/helpers/format-number.js'
import { Helper } from '@/mixin/helper'
moment.locale('id')

export default {
  name: 'DeliveryLoad',
  mixins: [Helper],
  components: {
    McBadge,
    McButton,
    ValueHelpTable,
  },
  props: {
    fleetTaskId: {
      type: String,
      default: '',
    },
    customerNotes: {
      type: [Array, String, Number, Object],
      default: null,
    },
    activity: {
      type: Object,
      default: () => ({}),
    },
    isPickUp: {
      type: Boolean,
      default: true,
    },
    isComplete: {
      type: Boolean,
      default: false,
    },
    isCompletedPickUp: {
      type: Boolean,
      default: false,
    },
    isCompletedDrop: {
      type: Boolean,
      default: false,
    },
    isAdjustedActivity: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantityConversionError: null,
      isLoading: false,
      dataLoaded: false,
      baseTableFields: [
        {
          key: 'product.name',
          label: 'Nama Produk',
          isSortable: false,
          isSlot: true,
          name: 'product_name',
        },
        {
          key: 'quantity',
          label: 'Rencana',
          isSortable: false,
          isSlot: true,
          name: 'quantity',
        },
        {
          key: 'actual_pickup',
          label: 'Aktual Pengambilan',
          isSortable: false,
          isSlot: true,
          name: 'actual',
        },
        {
          key: 'reason',
          label: 'Alasan Perubahan',
          isSortable: false,
          isSlot: true,
          name: 'reason',
        },
      ],
      totalCount: 0,
      isAdjustedPicked: false,
      isAdjustedDelivered: false,
      reasonDisabledMap: {},
      isFromDetail: false,
      modifiedQuantities: {},
      adjustedDeliveryLoads: [],
      lastUpdateFormatted: '-',
      errorFields: [],
      validationErrors: {},
      fieldErrors: {},
      latestLoads: [],
      conversionForms: {},
      conversionSubmitAttemptedMap: {},
      conversionLoadingMap: {},
      conversionDropErrors: {},
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return (
        Array.isArray(data?.company_id) && 
        data.company_id.includes(this.getCompanyId())
      )
    },
    tableAttrs() {
      const tableFields = [...this.baseTableFields]
      if (!this.isPickUp) {
        const dropField = {
          key: 'actual_drop',
          label: 'Aktual Terkirim',
          isSortable: false,
          isSlot: true,
          name: 'drop',
        }
        const reasonIndex = tableFields.findIndex((field) => field.key === 'reason')
        if (reasonIndex !== -1) {
          tableFields.splice(reasonIndex, 0, dropField)
        } else {
          tableFields.push(dropField)
        }
      }

      return {
        name: 'delivery-load',
        trackBy: 'id',
        tableFields: tableFields,
      }
    },
    isModifyQuantity(){
      return (
        (this.isAdjustedDelivered && !this.isPickUp) || 
        (this.isAdjustedPicked && this.isPickUp)
      )
    },
    badgeIcon() {
      return this.isModifyQuantity ? 'icon-fill_alert-triangle' : 'icon-fill_checkmark-circle'
    },
    badgeVariant() {
      return this.isModifyQuantity ? 'warning' : 'success'
    },
    badgeText() {
      return this.isModifyQuantity ? 'Kuantitas Diubah' : 'Sesuai Rencana'
    },
    badgeOutline() {
      return this.isModifyQuantity ? 'outlineWarning' : 'outlineSuccess'
    },
    getUpdatedWeight() {
      return this.getUpdatedValue('weight')
    },
    getUpdatedVolume() {
      return this.getUpdatedValue('volume')
    },
    isPoDAdjustmentPhase() {
      return !this.isPickUp && this.isCompletedPickUp && !this.isCompletedDrop
    },
  },
  watch: {
    isModifyQuantity: {
      immediate: true,
      handler() {
        if (this.activity?.delivery_order?.id) {
          this.fetchLastUpdate()
        }
      }
    }
  },
  methods: {
    formatDateWithTz,
    async getOptionUnit(opts = {}) {
      const data = this.getFeatureFlagValue('customFeature.samator')
      let units = []
      if (this.isSamator) {
        const liquidConversion = data?.liquid_conversion || []
        units = liquidConversion.map((item) => ({
          id: item.toLowerCase(),
          name: item,
        }))
      }

      const search = (opts?.search || '').toString().trim().toLowerCase()
      const filteredUnits = search
        ? units.filter((item) => item.name.toLowerCase().includes(search))
        : units

      return {
        isPagination: false,
        metadata: {
          total_page: 1,
          total_count: filteredUnits.length,
          page: 1,
        },
        data: {
          paginated_result: filteredUnits,
        },
      }
    },
    getConversionForm(deliveryOrderId) {
      if (!deliveryOrderId) {
        return {
          initialQuantity: '',
          finalQuantity: '',
          unitConversion: null,
        }
      }

      if (!Object.prototype.hasOwnProperty.call(this.conversionForms, deliveryOrderId)) {
        this.$set(this.conversionForms, deliveryOrderId, {
          initialQuantity: '',
          finalQuantity: '',
          unitConversion: null,
        })
      }

      return this.conversionForms[deliveryOrderId]
    },
    getConversionFieldValue(deliveryOrderId, fieldName) {
      return this.getConversionForm(deliveryOrderId)[fieldName]
    },
    isFilledConversionValue(value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim() !== ''
      if (typeof value === 'object') {
        return Boolean(value.id || value.value || value.name)
      }
      return true
    },
    isConversionFormComplete(deliveryOrderId) {
      const form = this.getConversionForm(deliveryOrderId)

      return (
        this.isFilledConversionValue(form.initialQuantity) &&
        this.isFilledConversionValue(form.finalQuantity) &&
        this.isFilledConversionValue(form.unitConversion)
      )
    },
    getConversionValidationBehavior(deliveryOrderId) {
      return this.conversionSubmitAttemptedMap[deliveryOrderId] ? 'live' : 'submit'
    },
    updateConversionField(deliveryOrderId, fieldName, value) {
      const form = this.getConversionForm(deliveryOrderId)
      this.$set(form, fieldName, value)
      this.clearConversionDropError(deliveryOrderId)
    },
    isConversionButtonDisabled(deliveryOrderId) {
      return (
        !this.isConversionFormComplete(deliveryOrderId) ||
        this.conversionLoadingMap[deliveryOrderId]
      )
    },
    parseConversionNumber(value) {
      if (value === null || value === undefined) return NaN
      const normalized = value.toString().replace(',', '.').trim()
      return Number(normalized)
    },
    async getThirdPartyIdByNote(noteId){
      const res = await DeliveryNote.show(
        this.activity?.delivery_order?.customer?.id
      )(noteId)
        return await res.data?.third_party_id
    },
    async getCustomerNoteThirdPartyId() {
      let id
      if (Array.isArray(this.customerNotes) && this.customerNotes.length>0) {
        id = this.customerNotes[0]
      }else if (this.customerNotes && typeof this.customerNotes === 'object') {
        id = this.customerNotes?.id
      } else return

      // get note from API
      const thirdPartyId = await this.getThirdPartyIdByNote(id)
      return thirdPartyId
    },
    getConversionUom(unitConversion) {
      if (!unitConversion) return ''
      if (typeof unitConversion === 'string') return unitConversion
      return unitConversion.name || unitConversion.id || ''
    },
    isLiquidLoadItem() {
      return this.isProductTypeLiquid(this.activity?.delivery_order)
    },
    shouldShowDropInput(item) {
      if (this.isPickUp) return false
      if (this.modifiedQuantities[`drop-${item.id}`] !== undefined) return true
      return item.quantity_picked !== 0 || item.quantity_delivered === null
    },
    isDropInputDisabled() {
      return this.isLiquidLoadItem()
    },
    async applyConvertedDropQuantity(deliveryOrderId, qty) {
      if (this.isPickUp) return

      this.clearConversionDropError(deliveryOrderId)

      let loads = this.latestLoads || []
      if (!loads.length) {
        const response = await this.getDeliveryLoadData({})
        loads = response?.data?.paginated_result || []
      }

      const loadItem = loads.find((item) => item?.quantity_picked !== 0) || loads[0]
      if (!loadItem) return

      this.handleDropChange(loadItem, String(qty))
    },
    async handleToConversion(activity) {
      const deliveryOrderId = activity?.delivery_order?.id
      if (!deliveryOrderId) return

      this.$set(this.conversionSubmitAttemptedMap, deliveryOrderId, true)
      if (!this.isConversionFormComplete(deliveryOrderId)) return

      const form = this.getConversionForm(deliveryOrderId)
      const customerNoteThirdPartyId = await this.getCustomerNoteThirdPartyId()
      if(!customerNoteThirdPartyId)
        return this.$toast.error('Terdapat kesalahan saat mengambil note third party id')

      const payload = {
        fleet_task_id: this.fleetTaskId || '',
        order_id: deliveryOrderId,
        customer_note_third_party_id: customerNoteThirdPartyId,
        start_qty: this.parseConversionNumber(form.initialQuantity),
        end_qty: this.parseConversionNumber(form.finalQuantity),
        uom: this.getConversionUom(form.unitConversion),
      }

      if (isNaN(payload.start_qty) || isNaN(payload.end_qty) || !payload.uom) return

      this.$set(this.conversionLoadingMap, deliveryOrderId, true)

      try {
        const response = await quantityConversion(payload)
        if (response.code !== 200) {
          this.quantityConversionError = response.message || this.$t('global.somethingWentWrong')
          return
        }
        this.quantityConversionError = null

        const qty = response?.data?.qty
        if (typeof qty === 'number' && !isNaN(qty)) {
          const loads = this.latestLoads || []
          const loadItem = loads.find((item) => item?.quantity_picked !== 0) || loads[0]
          const pickupQty = Number(loadItem?.quantity_picked)

          const isLiquidLoad = this.isLiquidLoadItem(loadItem)
          if (!isLiquidLoad && loadItem && !isNaN(pickupQty) && qty > pickupQty) {
            this.clearConversionDropError(deliveryOrderId)
            this.$set(this.conversionDropErrors, loadItem.id, {
              deliveryOrderId,
              message: 'Aktual pengiriman melebihi aktual pengambilan',
            })
            this.$set(this.fieldErrors, `conversion-${deliveryOrderId}`, { hasErrors: true })
            this.checkGlobalValidation()
            return
          }
          
          this.clearConversionDropError(deliveryOrderId)
          await this.applyConvertedDropQuantity(deliveryOrderId, qty)
        }
      } catch (error) {
        // no-op: keep form editable for retry
      } finally {
        this.$set(this.conversionLoadingMap, deliveryOrderId, false)
      }
    },
    getConversionDropError(item = {}) {
      return this.conversionDropErrors[item.id]?.message || ''
    },
    clearConversionDropError(deliveryOrderId) {
      if (!deliveryOrderId) return

      Object.keys(this.conversionDropErrors).forEach((loadId) => {
        const errorObj = this.conversionDropErrors[loadId]
        if (errorObj?.deliveryOrderId === deliveryOrderId) {
          this.$delete(this.conversionDropErrors, loadId)
        }
      })

      this.$set(this.fieldErrors, `conversion-${deliveryOrderId}`, { hasErrors: false })
      this.checkGlobalValidation()
    },
    formatNumber(value) {
      return formatNumber(value, 3) || '-'
    },
    santitizeNumber(num) {
      const number = Number(num)

      if (Number.isInteger(number)) {
        return formatNumber(number, 3)
      }

      return Number(number.toString().replace(/\.0+$|(\.\d*[1-9])0+$/, '$1'))
    },
    validationRuleActual(item) {
      const maxLimit = item.is_adjusted_picked ? item.quantity_picked : item.quantity
      return `bail|number|decimal:3|max:${maxLimit}|min:0,value|required`
    },
    validationRuleDrop(item) {
      if (this.isLiquidLoadItem(item)) {
        return 'bail|number|decimal:3'
      }

      const maxLimit =
        item.is_adjusted_picked && item.is_adjusted_delivered
          ? item.quantity_delivered
          : item.is_adjusted_picked
          ? item.quantity_picked
          : item.is_adjusted_delivered
          ? item.quantity_delivered
          : item.quantity
      return `bail|number|decimal:3|max:${maxLimit}|min:0,value|required`
    },
    getUpdatedValue(field) {
      return (item, inputValue, column) => {
        if (
          (!inputValue || inputValue.trim() === '')
          && column !== 'drop-conversion'
        ) {
          if (item.is_adjusted_picked && item.is_adjusted_delivered) {
            if (this.isCompletedPickUp && this.isPickUp) {
              return item[`${field}_picked`]
            }else if(column === 'drop' && this.hasValidationError(`${item.id}-drop-ref`)){
              return '-'
            }else{
              return item[`${field}_delivered`]
            }
          } else if (item.is_adjusted_delivered) {
            if(column === 'drop'){
              return this.hasValidationError(`${item.id}-drop-ref`) ? '-'
              : item[`${field}_delivered`] 
            } else {
              return item[`${field}_delivered`]
            }
          } else if (item.is_adjusted_picked) {
            if(column === 'drop'){
              return this.hasValidationError(`${item.id}-drop-ref`) ? '-'
              : item[`${field}_picked`] 
            } else {
              return this.hasValidationError(`${item.id}-actual-ref`) ? '-'
              : item[`${field}_picked`]
            }
          }
          return '-'
        } else if (column === 'actual' && item.is_adjusted_picked && isNaN(inputValue)){
          return this.hasValidationError(`${item.id}-actual-ref`) ? '-'
            : item[`${field}_picked`]
        }

        const tmp = column === 'actual'? 'pick': 'deliver'
        const newQuantity = parseFloat(
          this.modifiedQuantities[`${column}-${item.id}`] 
          ?? item[`quantity_${tmp}ed`]
        )
        if (isNaN(newQuantity) || isNaN(parseFloat(inputValue))) {
          return '-'
        }

        const conversionKey = `conversion_${field}_value`
        const result = newQuantity * item[conversionKey]
        return this.santitizeNumber(result)
      }
    },
    async getDeliveryLoadData(opts) {
      this.isLoading = true

      try {
        const response = await Load.get(this.activity.delivery_order.id)(opts || {})
        const results = response?.data?.paginated_result || []
        this.latestLoads = results
        this.totalCount = response?.metadata?.total_count || 0

        this.setAdjustmentFlags(results)

        results.forEach((item) => {
          const dropKey = `drop-${item.id}`
          if (!(dropKey in this.modifiedQuantities) && item.quantity_delivered) {
            this.$set(this.modifiedQuantities, dropKey, String(item.quantity_delivered))
          }
          const actualKey = `actual-${item.id}`
          if (!(actualKey in this.modifiedQuantities) && item.quantity_picked) {
            this.$set(this.modifiedQuantities, actualKey, String(item.quantity_picked))
          }
        })

        this.isFromDetail = true

        return response
      } catch (error) {
        return {
          data: { paginated_result: [] },
          metadata: { total_count: 0, page: 1 },
        }
      } finally {
        this.isLoading = false
      }
    },
    setAdjustmentFlags(results) {
      this.isAdjustedPicked = false
      this.isAdjustedDelivered = false

      if (this.activity.type === 'PICKUP') {
        this.isAdjustedPicked = results.some((item) => item.is_adjusted_picked === true)
      } else if (this.activity.type === 'DROP') {
        this.isAdjustedDelivered = results.some((item) => item.is_adjusted_delivered === true)
      }
    },
    handleValidation(fieldName, validationObject) {
      let nextFieldErrors
      if(validationObject.hasErrors){
        nextFieldErrors = {
          ...this.fieldErrors,
          [fieldName]: validationObject,
        }
      }else{
        const tmp = { ...this.fieldErrors }
        delete tmp[fieldName]
        nextFieldErrors = tmp
      }

      this.$set(this, 'fieldErrors', nextFieldErrors)
      this.checkGlobalValidation(nextFieldErrors)
    },
    parseRefKey(refKey = '') {
      const normalizedRefKey = String(refKey).replace(/-ref$/, '')
      const separatorIndex = normalizedRefKey.lastIndexOf('-')

      if (separatorIndex === -1) {
        return {
          itemId: normalizedRefKey,
          type: '',
        }
      }

      return {
        itemId: normalizedRefKey.slice(0, separatorIndex),
        type: normalizedRefKey.slice(separatorIndex + 1),
      }
    },
    hasValidationError(refKey) {
      const { itemId, type } = this.parseRefKey(refKey)
      const fieldKey = `${type}-${itemId}`
      const validation = this.fieldErrors[fieldKey]

      if (!validation || !validation.hasErrors) return false

      if (type === 'reason' && this.reasonDisabledMap[itemId] !== false) {
        return false
      }

      return true
    },

    handleActualChange(item, value) {
      const isEmpty = !value || value.trim() === ''

      // Ensure both values are numbers for comparison
      const newValue = parseFloat(value)
      const referenceValue = parseFloat(item.quantity)

      let isEqual = true // Default to disabled
      if (!isNaN(newValue) && !isNaN(referenceValue)) {
        isEqual = newValue == referenceValue
      }

      this.$set(this.reasonDisabledMap, item.id, isEmpty || isEqual)
      this.$set(this.modifiedQuantities, `actual-${item.id}`, value)

      if (!isEmpty && !isEqual) {
        const numericValue = Number(value)

        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads[existingIndex].quantity = numericValue
        } else {
          this.adjustedDeliveryLoads.push({
            delivery_load_uuid: item.id,
            quantity: numericValue,
            reason: null,
          })
        }
      } else {
        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads.splice(existingIndex, 1)
        }
      }

      this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
      this.isFromDetail = false
      this.checkGlobalValidation()
    },

    handleDropChange(item, value) {
      const isEmpty = !value || value.trim() === ''
      // Ensure both values are numbers for comparison
      const newValue = parseFloat(value)
      const referenceValue = parseFloat(
        item.is_adjusted_delivered ? item.quantity_picked : item.quantity
      )

      let isEqual = true // Default to disabled
      if (!isNaN(newValue) && !isNaN(referenceValue)) {
        isEqual = newValue == referenceValue
      }

      this.$set(this.reasonDisabledMap, item.id, isEmpty || isEqual)
      this.$set(this.modifiedQuantities, `drop-${item.id}`, value)

      const samatorLiquid = this.isSamator && this.isLiquidLoadItem(item)
      const samatorLiquidAdjusted = 
        !isNaN(newValue)
        && samatorLiquid
        && (item.quantity_picked !== newValue)

      if (
        !isEmpty && (
          samatorLiquidAdjusted || (!samatorLiquid && !isEqual)
        )
      ) {
        const numericValue = Number(value)

        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads[existingIndex].quantity = numericValue
        } else {
          this.adjustedDeliveryLoads.push({
            delivery_load_uuid: item.id,
            quantity: numericValue,
            reason: null,
          })
        }

        this.$emit('update:converted', item.id, true)
      } else {
        const existingIndex = this.adjustedDeliveryLoads.findIndex(
          (load) => load.delivery_load_uuid === item.id
        )
        this.$emit('update:converted', item.id, false)

        if (existingIndex !== -1) {
          this.adjustedDeliveryLoads.splice(existingIndex, 1)
        }
      }

      this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)
      this.isFromDetail = false
      this.checkGlobalValidation()
    },

    updateReason(item, reason) {
      const existingIndex = this.adjustedDeliveryLoads.findIndex(
        (load) => load.delivery_load_uuid === item.id
      )

      if (existingIndex !== -1) {
        this.adjustedDeliveryLoads[existingIndex].reason = reason
        this.$emit('update:adjusted-loads', this.adjustedDeliveryLoads)

        if (reason && this.errorFields.includes(item.id)) {
          const index = this.errorFields.indexOf(item.id)
          if (index !== -1) {
            this.errorFields.splice(index, 1)
          }
        }
      }

      this.checkGlobalValidation()
    },

    checkGlobalValidation(fieldErrors = this.fieldErrors) {
      // Gunakan debounce agar tidak overload
      if (this._validationTimeout) clearTimeout(this._validationTimeout)
      this._validationTimeout = setTimeout(() => {
        // Cek apakah ada field yang memiliki error dan tidak sedang disabled
        const hasErrors = Object.keys(fieldErrors).some((key) => {
          const validation = fieldErrors[key]
          if (!validation || !validation.hasErrors) return false

          // Cek apakah field tersebut sebenarnya disabled
          const [type, itemId] = key.split('-')
          if (type === 'reason' && this.reasonDisabledMap[itemId] !== false) {
            return false
          }

          return true
        })

        this.$emit('update:has-errors', hasErrors)
      }, 100)
    },

    shouldRequireReason(item) {
      // Alasan hanya wajib saat field alasan aktif (tidak disabled)
      if (this.reasonDisabledMap[item.id] !== false) {
        return false
      }

      const isAdjusted = item.is_adjusted_picked || item.is_adjusted_delivered
      const isAdjustedPicked = item.is_adjusted_picked
      const isAdjustedDelivered = item.is_adjusted_delivered

      return isAdjusted && (isAdjustedPicked || isAdjustedDelivered)
    },
    async fetchLastUpdate() {
      if (!this.activity?.delivery_order?.id) return

      try {
        const queryParam = {
          page: 1,
          limit: 1,
          sorts: ['created_on desc'],
        }
        const res = await historyApi.getLog(this.activity?.delivery_order?.id, queryParam)

        const data = res.data.paginated_result
        if (data && data.length > 0) {
          const matchingLog = data.find(
            (log) => log.delivery_order_uuid === this.activity?.delivery_order?.id
          )

          if (matchingLog) {
            this.lastUpdateFormatted = this.formatDateWithTz(matchingLog.created_on)
            return
          }
        }

        this.lastUpdateFormatted = '-'
      } catch (error) {
        console.error('Error fetching update history:', error)
        this.lastUpdateFormatted = '-'
      }
    },

    getLastUpdate() {
      if (this.lastUpdateFormatted === '-' && this.activity?.delivery_order?.id) {
        this.fetchLastUpdate()
      }
      return this.lastUpdateFormatted
    },

    highlightErrorFields(missingReasons) {
      this.errorFields = []

      missingReasons.forEach((load) => {
        this.errorFields.push(load.delivery_load_uuid)
      })

      this.$forceUpdate()
    },
    isProductTypeLiquid(deliveryOrderDetail = {}) {
      const productTypeField = deliveryOrderDetail?.user_definition_fields?.find(
        (field) => field?.name === 'Product Type'
      )
      const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

      return ['bulk', 'tube'].includes(productTypeValue)
    },
  },
}
</script>

<style scoped lang="scss">
.mc-fa {
  &:before {
    font-size: 20px;
  }

  &.icon-outline_info {
    &:before {
      font-size: 16px;
    }
  }
}

.icon-container {
  width: 32px;
  height: 32px;
}

.timestamp-text {
  font-size: 12px;
}

.cell-display {
  color: $mep-color-text;

  .primary-display {
    font-size: 13px;
  }

  .secondary-display {
    color: $text-muted;
    font-size: 13px;

    .dot-separator {
      margin: 0 6px;
      font-size: 13px;
      line-height: 1;
      font-weight: 900;
    }
  }
}

.custom-form-group {
  position: relative;
}

.error-input {
  border: 1px solid #dc3545 !important;
  border-radius: 5px;
  padding: 0px;
  :deep(input),
  :deep(.formulate-input-element input) {
    border: none !important;
    border-color: transparent !important;
  }
}

.conversion-card {
  border: 1px solid $teal-100;
  border-radius: 2px;

  .dash-line {
    width: 20px;
    height: 2px;
    background-color: $teal-200;
    border-radius: 999px;
    margin: 20px 10px 0px;
  }
}

.error-message {
  display: block;
  color: #dc3545;
  font-size: 0.7109375rem;
  font-style: italic;
  margin-top: 0px;
}

.yellow-border {
  border: 1px solid $warning !important;
  border-radius: 5px !important;
  padding: 0px;
  margin: 4px;

  :deep(input),
  :deep(.formulate-input-element input) {
    border: none !important;
    border-color: transparent !important;
    border-radius: 5px !important;
  }

  :deep(.formulate-input-element) {
    border-radius: 5px !important;
    &:focus-within {
      border-color: $warning !important;
      box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25) !important;
      border-radius: 5px !important;
    }
  }

  :deep(input:focus) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 5px !important;
  }
}

.modified-value {
  color: $warning !important;
  font-weight: 500;
}
</style>
